/* ─── CSS Custom Properties (Light Theme Default) ────────────── */
:host {
  --widget-primary: #1976d2;
  --widget-primary-light: #e3f2fd;
  --widget-bg: #ffffff;
  --widget-surface: #f5f5f5;
  --widget-text: #212121;
  --widget-text-secondary: #757575;
  --widget-text-inverse: #ffffff;
  --widget-border: #e0e0e0;
  --widget-radius: 12px;
  --widget-shadow: 0 4px 24px rgba(0, 0, 0, 0.15);
  --widget-fab-size: 56px;
  --widget-panel-width: 400px;
  --widget-panel-height: 550px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
    Oxygen-Sans, Ubuntu, Cantarell, "Helvetica Neue", sans-serif;
  font-size: 14px;
  line-height: 1.5;
  color: var(--widget-text);
}

/* ─── Dark Theme ─────────────────────────────────────────────── */
:host(.dark) {
  --widget-primary: #90caf9;
  --widget-primary-light: #1e3a5f;
  --widget-bg: #1e1e1e;
  --widget-surface: #2d2d2d;
  --widget-text: #e0e0e0;
  --widget-text-secondary: #aaaaaa;
  --widget-text-inverse: #1e1e1e;
  --widget-border: #404040;
  --widget-shadow: 0 4px 24px rgba(0, 0, 0, 0.4);
}

/* ─── Reset ──────────────────────────────────────────────────── */
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ─── FAB Button ─────────────────────────────────────────────── */
.widget-fab {
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: var(--widget-fab-size);
  height: var(--widget-fab-size);
  border-radius: 50%;
  background: var(--widget-primary);
  color: var(--widget-text-inverse);
  border: none;
  cursor: pointer;
  box-shadow: var(--widget-shadow);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  z-index: 2147483646;
  animation: widget-fab-entrance 0.3s ease-out;
}

.widget-fab:hover {
  transform: scale(1.08);
  box-shadow: 0 6px 32px rgba(0, 0, 0, 0.2);
}

.widget-fab:active {
  transform: scale(0.95);
}

.widget-fab--left {
  right: auto;
  left: 20px;
}

.widget-fab svg {
  width: 28px;
  height: 28px;
  fill: currentColor;
}

.widget-fab-logo {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

@keyframes widget-fab-entrance {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* ─── Chat Panel ─────────────────────────────────────────────── */
.widget-panel {
  position: fixed;
  bottom: 88px;
  right: 20px;
  width: var(--widget-panel-width);
  height: var(--widget-panel-height);
  background: var(--widget-bg);
  border-radius: var(--widget-radius);
  box-shadow: var(--widget-shadow);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 2147483646;
  animation: widget-panel-slide-up 0.25s ease-out;
}

.widget-panel--left {
  right: auto;
  left: 20px;
}

@keyframes widget-panel-slide-up {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ─── Resize Handle ──────────────────────────────────────────── */
.widget-resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 20px;
  height: 20px;
  cursor: nw-resize;
  z-index: 10;
  border-radius: var(--widget-radius) 0 0 0;
}

/* Six dots in a triangular grip pattern */
.widget-resize-handle::before,
.widget-resize-handle::after {
  content: "";
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
}

.widget-resize-handle::before {
  width: 3px;
  height: 3px;
  top: 5px;
  left: 5px;
  box-shadow:
    5px 0 0 rgba(255, 255, 255, 0.4),
    10px 0 0 rgba(255, 255, 255, 0.4),
    0 5px 0 rgba(255, 255, 255, 0.4),
    5px 5px 0 rgba(255, 255, 255, 0.4),
    0 10px 0 rgba(255, 255, 255, 0.4);
}

.widget-resize-handle::after {
  display: none;
}

.widget-resize-handle:hover::before {
  background: rgba(255, 255, 255, 0.9);
  box-shadow:
    5px 0 0 rgba(255, 255, 255, 0.9),
    10px 0 0 rgba(255, 255, 255, 0.9),
    0 5px 0 rgba(255, 255, 255, 0.9),
    5px 5px 0 rgba(255, 255, 255, 0.9),
    0 10px 0 rgba(255, 255, 255, 0.9);
}

/* For bottom-left panel, handle goes top-right */
.widget-resize-handle--right {
  left: auto;
  right: 0;
  cursor: ne-resize;
  border-radius: 0 var(--widget-radius) 0 0;
}

.widget-resize-handle--right::before {
  left: auto;
  right: 5px;
}

/* ─── Panel Header ───────────────────────────────────────────── */
.widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--widget-primary);
  color: var(--widget-text-inverse);
  flex-shrink: 0;
}

.widget-header-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.widget-header-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.widget-info-btn {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  opacity: 0.75;
}

.widget-info-btn:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.15);
}

.widget-info-btn svg {
  width: 16px;
  height: 16px;
  fill: currentColor;
}

.widget-header-subtitle {
  font-size: 12px;
  opacity: 0.85;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.widget-header-actions {
  display: flex;
  gap: 2px;
  flex-shrink: 0;
}

.widget-model-select {
  max-width: 120px;
  margin-right: 4px;
  padding: 3px 4px;
  font: inherit;
  font-size: 12px;
  color: inherit;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 4px;
  cursor: pointer;
}

.widget-model-select:disabled {
  opacity: 0.6;
  cursor: default;
}

/* The open list is drawn by the OS on its own surface, where the header's light
   text would be unreadable. */
.widget-model-select option {
  color: #1a1a1a;
  background: #ffffff;
}

.widget-close-btn {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.widget-close-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

.widget-close-btn svg {
  width: 20px;
  height: 20px;
  fill: currentColor;
}

/* ─── Message List ───────────────────────────────────────────── */
.widget-messages {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.widget-messages::-webkit-scrollbar {
  width: 6px;
}

.widget-messages::-webkit-scrollbar-thumb {
  background: var(--widget-border);
  border-radius: 3px;
}

/* ─── Welcome Message ────────────────────────────────────────── */
.widget-welcome {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
  text-align: center;
  color: var(--widget-text-secondary);
}

.widget-welcome-icon {
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.widget-welcome-text {
  font-size: 14px;
  line-height: 1.5;
}

.widget-privacy-notice {
  margin-top: 16px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: #fff8f0;
  border: 1px solid #ffcc80;
  border-radius: 8px;
  font-size: 12px;
  line-height: 1.4;
  color: #b45309;
  max-width: 320px;
  text-align: left;
}

.widget-privacy-icon {
  width: 18px;
  height: 18px;
  fill: #e65100;
  flex-shrink: 0;
  margin-top: 1px;
}

:host(.dark) .widget-privacy-notice {
  background: #3e2723;
  border-color: #6d4c41;
  color: #ffab91;
}

:host(.dark) .widget-privacy-icon {
  fill: #ffab91;
}

/* ─── Message Bubbles ────────────────────────────────────────── */
.widget-message {
  display: flex;
  flex-direction: column;
  max-width: 85%;
}

.widget-message--user {
  align-self: flex-end;
}

.widget-message--assistant {
  align-self: flex-start;
}

.widget-bubble {
  padding: 8px 12px;
  border-radius: 12px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.widget-message--user .widget-bubble {
  background: var(--widget-primary);
  color: var(--widget-text-inverse);
  border-bottom-right-radius: 4px;
}

.widget-message--assistant .widget-bubble {
  background: var(--widget-surface);
  color: var(--widget-text);
  border-bottom-left-radius: 4px;
}

/* ─── Markdown Content in Assistant Bubbles ──────────────────── */
.widget-bubble p {
  margin: 0 0 8px 0;
}

.widget-bubble p:last-child {
  margin-bottom: 0;
}

.widget-bubble code {
  font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
  font-size: 0.9em;
  background: rgba(0, 0, 0, 0.06);
  padding: 1px 4px;
  border-radius: 3px;
}

.widget-bubble pre {
  background: rgba(0, 0, 0, 0.06);
  padding: 8px 10px;
  border-radius: 6px;
  overflow-x: auto;
  margin: 8px 0;
}

.widget-bubble pre code {
  background: none;
  padding: 0;
}

.widget-bubble ul,
.widget-bubble ol {
  padding-left: 20px;
  margin: 4px 0;
}

.widget-bubble blockquote {
  border-left: 3px solid var(--widget-border);
  padding-left: 10px;
  margin: 8px 0;
  color: var(--widget-text-secondary);
}

.widget-bubble h1,
.widget-bubble h2,
.widget-bubble h3,
.widget-bubble h4 {
  margin: 8px 0 4px 0;
  font-size: 1em;
  font-weight: 600;
}

.widget-bubble a {
  color: var(--widget-primary);
  text-decoration: underline;
}

/* ─── Streaming Indicator ────────────────────────────────────── */
.widget-streaming {
  display: flex;
  gap: 4px;
  padding: 4px 0;
}

.widget-streaming-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--widget-text-secondary);
  animation: widget-dot-pulse 1.2s ease-in-out infinite;
}

.widget-streaming-dot:nth-child(2) {
  animation-delay: 0.2s;
}

.widget-streaming-dot:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes widget-dot-pulse {
  0%,
  60%,
  100% {
    opacity: 0.3;
    transform: scale(0.8);
  }
  30% {
    opacity: 1;
    transform: scale(1);
  }
}

/* ─── Chat Input ─────────────────────────────────────────────── */
.widget-input-area-wrapper {
  flex-shrink: 0;
  border-top: 1px solid var(--widget-border);
  background: var(--widget-bg);
}

.widget-message-counter {
  text-align: center;
  padding: 4px 12px 0;
  font-size: 11px;
  color: var(--widget-text-secondary);
}

.widget-input-area {
  display: flex;
  align-items: flex-end;
  gap: 8px;
  padding: 10px 12px;
  background: var(--widget-bg);
}

.widget-textarea {
  flex: 1;
  resize: none;
  border: 1px solid var(--widget-border);
  border-radius: 8px;
  padding: 8px 12px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
  color: var(--widget-text);
  background: var(--widget-bg);
  outline: none;
  min-height: 38px;
  max-height: 100px;
  overflow-y: auto;
}

.widget-textarea:focus {
  border-color: var(--widget-primary);
}

.widget-textarea::placeholder {
  color: var(--widget-text-secondary);
}

.widget-send-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--widget-primary);
  color: var(--widget-text-inverse);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: opacity 0.15s ease;
}

.widget-send-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.widget-send-btn:not(:disabled):hover {
  opacity: 0.85;
}

.widget-send-btn svg {
  width: 18px;
  height: 18px;
  fill: currentColor;
}

/* ─── Demo Banner ────────────────────────────────────────────── */
.widget-demo-banner {
  padding: 4px 12px;
  background: #fff3e0;
  color: #e65100;
  font-size: 11px;
  text-align: center;
  border-bottom: 1px solid #ffe0b2;
  flex-shrink: 0;
  font-weight: 500;
  letter-spacing: 0.02em;
}

:host(.dark) .widget-demo-banner {
  background: #3e2723;
  color: #ffab91;
  border-bottom-color: #4e342e;
}

/* ─── Info Panel ─────────────────────────────────────────────── */
.widget-info-panel {
  padding: 12px 16px;
  background: #f0f4f8;
  color: #555;
  font-size: 12px;
  line-height: 1.5;
  border-bottom: 1px solid var(--widget-border);
  flex-shrink: 0;
  overflow-y: auto;
  max-height: 200px;
}

.widget-info-panel p {
  margin: 0 0 6px 0;
}

.widget-info-panel p:last-child {
  margin-bottom: 0;
}

.widget-info-panel ul {
  margin: 0 0 0 16px;
  padding: 0;
  list-style: disc;
}

.widget-info-panel li {
  margin-bottom: 2px;
}

.widget-info-warning {
  color: #c62828;
  font-weight: 500;
}

:host(.dark) .widget-info-panel {
  background: #2a2a2a;
  color: #bbb;
}

:host(.dark) .widget-info-warning {
  color: #ef5350;
}

/* ─── Message Limit Notice ───────────────────────────────────── */
.widget-limit-notice {
  text-align: center;
  padding: 10px 16px;
  color: var(--widget-text-secondary);
  font-size: 13px;
  font-style: italic;
}

/* ─── Error Banner ───────────────────────────────────────────── */
.widget-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  background: #fef2f2;
  color: #b91c1c;
  font-size: 13px;
  border-bottom: 1px solid #fecaca;
}

:host(.dark) .widget-error {
  background: #451a1a;
  color: #fca5a5;
  border-bottom-color: #7f1d1d;
}

.widget-error-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.widget-error-retry {
  background: none;
  border: 1px solid #b91c1c;
  color: #b91c1c;
  cursor: pointer;
  padding: 2px 8px;
  font-size: 12px;
  border-radius: 4px;
  white-space: nowrap;
}

.widget-error-retry:hover {
  background: #b91c1c;
  color: #fff;
}

:host(.dark) .widget-error-retry {
  border-color: #fca5a5;
  color: #fca5a5;
}

:host(.dark) .widget-error-retry:hover {
  background: #fca5a5;
  color: #1e1e1e;
}

.widget-error-dismiss {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 2px;
  font-size: 16px;
  line-height: 1;
}

/* ─── Loading State ──────────────────────────────────────────── */
.widget-loading {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--widget-text-secondary);
  font-size: 14px;
}

/* ─── Responsive: Full Screen on Mobile ──────────────────────── */
@media (max-width: 480px) {
  .widget-panel {
    bottom: 0;
    right: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 0;
  }

  .widget-panel--left {
    left: 0;
    right: 0;
  }
}

/* ── Student self-identification (optional, extra-credit attribution) ── */
.widget-identity-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6px 12px;
  background: var(--widget-surface);
  border-bottom: 1px solid var(--widget-border);
  font-size: 12px;
  color: var(--widget-text-secondary);
}

.widget-identity-status {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.widget-identity-toggle {
  flex-shrink: 0;
  border: none;
  background: transparent;
  color: var(--widget-primary);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 2px 6px;
  border-radius: 6px;
}

.widget-identity-toggle:hover {
  background: var(--widget-primary-light);
}

.widget-identity-toggle--cta {
  background: var(--widget-primary);
  color: var(--widget-text-inverse);
}

.widget-identity-toggle--cta:hover {
  opacity: 0.9;
  background: var(--widget-primary);
}

.widget-identity-gate {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: var(--widget-primary-light);
  border-bottom: 1px solid var(--widget-border);
}

.widget-identity-gate-title {
  font-size: 13px;
  font-weight: 600;
  color: var(--widget-text);
}

.widget-identity-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border: 1px solid var(--widget-border);
  border-radius: 8px;
  font-size: 13px;
  background: var(--widget-bg);
  color: var(--widget-text);
}

.widget-identity-input:focus {
  outline: none;
  border-color: var(--widget-primary);
}

.widget-identity-gate-actions {
  display: flex;
  gap: 8px;
}

.widget-identity-save {
  flex: 1;
  border: none;
  background: var(--widget-primary);
  color: var(--widget-text-inverse);
  font-size: 13px;
  font-weight: 600;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
}

.widget-identity-save:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.widget-identity-skip {
  border: 1px solid var(--widget-border);
  background: var(--widget-bg);
  color: var(--widget-text-secondary);
  font-size: 13px;
  padding: 8px 14px;
  border-radius: 8px;
  cursor: pointer;
}

.widget-identity-gate-note {
  font-size: 11px;
  color: var(--widget-text-secondary);
}

/* ── Maximize / restore ──
   Anchor vertically with an explicit top AND bottom rather than a computed
   height. The base panel sits at bottom:88px (clearing the FAB), so setting only
   height:calc(100vh - 32px) put the top edge 56px ABOVE the viewport and pushed
   the header — and therefore the restore button — off screen.
   Horizontal anchoring is deliberately untouched so bottom-left embeds still work.
   Scoped to >480px: below that the panel is already full-screen, so maximizing is
   a no-op and this must not override the mobile rule. */
@media (min-width: 481px) {
  .widget-panel--maximized {
    top: 16px;
    bottom: 16px;
    height: auto;
    max-height: none;
    width: min(920px, calc(100vw - 40px));
  }
}

/* ── Copy button on code blocks ── */
.widget-bubble pre.widget-code-block {
  position: relative;
  /* A strip for Copy/Download so they never sit on top of the first line of code. */
  padding-top: 30px;
}
.widget-code-actions {
  position: absolute;
  top: 6px;
  right: 6px;
  display: flex;
  gap: 4px;
}
.widget-copy-btn {
  font-size: 11px;
  line-height: 1;
  padding: 4px 8px;
  background: var(--widget-surface);
  color: var(--widget-text-secondary);
  border: 1px solid var(--widget-border);
  border-radius: 6px;
  cursor: pointer;
  opacity: 0.6;
  transition: opacity 0.15s, color 0.15s;
}
.widget-code-block:hover .widget-copy-btn {
  opacity: 1;
}
.widget-copy-btn:hover {
  color: var(--widget-text);
}

/* Math: MathML drawn by the browser (see math-entry.ts). Until the renderer loads,
   or if it cannot, the placeholder shows the TeX as plain monospace text. */
.widget-math:not([data-rendered]) {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.9em;
}
.widget-math--display {
  display: block;
  margin: 8px 0;
  overflow-x: auto;
  overflow-y: hidden;
}
.widget-math math {
  font-size: 1.1em;
}

.widget-too-long {
  margin: 6px 12px 0;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1.4;
  /* Same palette as .widget-error, so the two read as one family. */
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
}
:host(.dark) .widget-too-long {
  color: #fca5a5;
  background: #451a1a;
  border-color: #7f1d1d;
}
